/**
 * Battle/Stage settings: difficulty modifiers chosen before starting a field.
 * AS3 source: StageSettings.as, XP_MULTIPLIERS array (index 66 in AS3 source).
 */

export const HpMultiplierOption = {
  Normal: 0,  // 1×
  P120: 1,    // 1.2×
  P150: 2,    // 1.5×
  Double: 3,  // 2×
  Triple: 4,  // 3×
  Tenfold: 5, // 10×
} as const;
export type HpMultiplierOption = (typeof HpMultiplierOption)[keyof typeof HpMultiplierOption];

export const WavesOption = {
  Normal: 0,
  Plus5: 1,
  Plus10: 2,
  Plus15: 3,
  Plus20: 4,
} as const;
export type WavesOption = (typeof WavesOption)[keyof typeof WavesOption];

export interface StageSettings {
  hpMultiplierOption: HpMultiplierOption;
  wavesOption: WavesOption;
  /** AS3: isExtraBanishCostOnReturningMonstersSelected — costToBanishMultiplier × 2.63 instead of 1.68 */
  extraBanishCost: boolean;
  /** AS3: isReturningMonstersHealingSelected — on banish mob heals 50% maxHp */
  healOnReturn: boolean;
}

export function createDefaultStageSettings(): StageSettings {
  return { hpMultiplierOption: 0, wavesOption: 0, extraBanishCost: false, healOnReturn: false };
}

// --- AS3 XP_MULTIPLIERS array (AS3 line 66) ---
// Indices 6..11: monster HP options
// Indices 18..22: waves num options
// Index 26: HEALING_BANISHED flag
// Index 27: EXTRA_BANISH_COST flag

const XP_MULTIPLIERS = [
  1, 1.13, 1.35, 1.67, 2.27, 3.14,   // 0..5  monster amount (unused in A4)
  1, 1.11, 1.32, 1.61, 2.24, 5.07,   // 6..11 monster HP
  1, 1.13, 1.17, 1.17, 1.23, 1.35,   // 12..17 wave composition (unused in A4)
  1, 1.04, 1.07, 1.13, 1.21, 1,      // 18..23 waves num (index 22 = Plus20 = 1.21; index 23 unused)
  1.24, 1.18, 1.09, 1.15, 1.55, 1.24, // 24..29 flags: 26=HEALING, 27=EXTRA_BANISH
];

const HP_MULTIPLIERS = [1, 1.2, 1.5, 2, 3, 10];
const EXTRA_WAVES = [0, 5, 10, 15, 20];

/** HP multiplier applied to monsterBaseHp. */
export function getHpMultiplier(s: StageSettings): number {
  return HP_MULTIPLIERS[s.hpMultiplierOption] ?? 1;
}

/** Extra waves added to wavesNum. */
export function getExtraWaves(s: StageSettings): number {
  return EXTRA_WAVES[s.wavesOption] ?? 0;
}

/**
 * Multiplier applied to costToBanishMultiplier when a monster is banished.
 * AS3-faithful: 2.63 with extraBanishCost enabled, 1.68 otherwise.
 */
export function getBanishMultiplierGrowth(s: StageSettings): number {
  return s.extraBanishCost ? 2.63 : 1.68;
}

/**
 * XP multiplier for this settings combo.
 * Formula: product of AS3 XP_MULTIPLIERS for each selected option.
 * Rounded to 2 decimals.
 */
export function getXpMultiplier(s: StageSettings): number {
  const xpMult =
    XP_MULTIPLIERS[s.hpMultiplierOption + 6] *
    XP_MULTIPLIERS[s.wavesOption + 18] *
    (s.healOnReturn ? XP_MULTIPLIERS[26] : 1) *
    (s.extraBanishCost ? XP_MULTIPLIERS[27] : 1);
  return Math.round(xpMult * 100) / 100;
}
